import React, { useEffect } from 'react'
import { Delete } from 'lucide-react'
import { cn } from '../../../lib/utils'

const ROW1 = ['q','w','e','r','t','y','u','i','o','p']
const ROW2 = ['a','s','d','f','g','h','j','k','l']
const ROW3 = ['z','x','c','v','b','n','m']

// Letter-state tones match the Board so the keyboard stays a visual
// memory of what you've ruled out. Default key uses the muted token
// so it sits quietly until coloured.
const TONE = {
  correct: 'bg-emerald-500 dark:bg-emerald-600 text-white border-transparent',
  present: 'bg-amber-500  dark:bg-amber-600  text-white border-transparent',
  absent:  'bg-stone-500  dark:bg-stone-600  text-white border-transparent',
  unused:  'bg-muted text-foreground border-border hover:bg-accent',
}

function Key({ value, label, onClick, state = 'unused', wide = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      disabled={disabled}
      className={cn(
        'h-12 sm:h-14 rounded-md border-2 font-semibold uppercase select-none',
        'flex items-center justify-center transition-colors',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        wide ? 'px-2.5 text-xs sm:text-sm flex-[1.5]' : 'flex-1 text-base sm:text-lg',
        TONE[state],
      )}
      dir="ltr"
      aria-label={label || value}
    >
      {label ?? value.toUpperCase()}
    </button>
  )
}

// On-screen keyboard. Also installs a window keydown listener so the
// physical keyboard works on desktop without the page hijacking
// shortcuts (the listener is scoped to single-letter keys + Enter +
// Backspace, and bails when an input is focused).
export default function Keyboard({ letterStates, onLetter, onEnter, onBackspace, disabled }) {
  useEffect(() => {
    if (disabled) return undefined
    const onKey = (e) => {
      // Don't steal typing in actual form fields (settings, login).
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'Enter') { e.preventDefault(); onEnter(); return }
      if (e.key === 'Backspace') { e.preventDefault(); onBackspace(); return }
      if (/^[a-zA-Z]$/.test(e.key)) { e.preventDefault(); onLetter(e.key.toLowerCase()) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [disabled, onLetter, onEnter, onBackspace])

  return (
    <div className="flex flex-col gap-1.5 w-full max-w-md mx-auto" dir="ltr">
      <div className="flex gap-1 sm:gap-1.5">
        {ROW1.map((l) => <Key key={l} value={l} state={letterStates[l]} onClick={onLetter} disabled={disabled} />)}
      </div>
      <div className="flex gap-1 sm:gap-1.5 px-3">
        {ROW2.map((l) => <Key key={l} value={l} state={letterStates[l]} onClick={onLetter} disabled={disabled} />)}
      </div>
      <div className="flex gap-1 sm:gap-1.5">
        <Key value="enter" label="Enter" onClick={() => onEnter()} wide disabled={disabled} />
        {ROW3.map((l) => <Key key={l} value={l} state={letterStates[l]} onClick={onLetter} disabled={disabled} />)}
        <Key value="backspace" label={<Delete className="w-4 h-4" />} onClick={() => onBackspace()} wide disabled={disabled} />
      </div>
    </div>
  )
}
